/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {translate} from '@ion/core/src/locale/locale';
import {DeviceFile} from '@ion/core/src/types/entities';
import {PLATFORM_IDS} from '@ion/core/src/utils/DeviceUtils';
import {useDeviceFiles} from '@ion/query/src/hooks/useDeviceFiles';
import {onRenderFileNameOption} from './helpers';
import {
  JobTaskFileNameChooserProps
} from './JobTaskFileNameChooser';
import {
  filterOptions,
  LMUConfigSearchField
} from './JobTaskLMUConfigUtils';
import JobTaskSearchInput from './JobTaskSearchInput';
import {useJobTaskChooserStyles} from './useJobTaskChooserStyles';

// defines the component style definitions ...
const useStyles = makeStyles(() => ({
  container : {
    minWidth : '260px'
  }
}));

/**
 * Defines the `JobTaskLMUConfigSearch` prop type definitions, which simply
 * extend the `JobTaskFileNameChooserProps` while requiring an additional
 * `field` property which indicates the field on which the search is to be
 * based.
 *
 * @see {@link JobTaskFileNameChooserProps}
 */
export type JobTaskLMUConfigSearchProps = JobTaskFileNameChooserProps & {
  field: LMUConfigSearchField;
};

/**
 * @internal
 *
 * The `JobTaskLMUConfigSearch` component provides the UI which allows users to
 * perform external Custom Searches for LMU Config Device Files by `version` or
 * `name`. This component provides the Search / AutoComplete / Dropdown Options
 * portion of the LMU Config Chooser Feature.
 *
 * Note, this component is generally not to be used directly, but rather, it will
 * be loaded via the `JobTaskSearchChooser` when the given Task is for an LMU
 * Config.
 *
 * @see {@link JobTaskSearchChooser}
 * @see {@link JobTaskLMUConfigSearchProps}
 * @see {@link JobTaskFileNameChooserProps}
 *
 */
const JobTaskLMUConfigSearch = ({
  field,
  onChange,
  accountId,
  includeSubAccounts,
  row
}: JobTaskLMUConfigSearchProps) => {
  const classes = useStyles();
  const styles  = useJobTaskChooserStyles();
  const {task}  = row || {};

  // provides the search parameters and reference to the search query api from
  // which all search requests are to based. by default, LMU Config Device Files
  // are pre-fetched via the `useLMUConfigDeviceFilePrefetch` hook, thus, when
  // this hook is invoked the device files will immediately be available ...
  const {deviceFiles, isLoading} = useDeviceFiles({
    platformIds : [`${PLATFORM_IDS.config}`],
    enabledOnly : true,
    includeSubAccounts,
    accountId
  });

  // provides a utility which returns the label to be displayed for the selected
  // option based ...
  const getOptionLabel = useCallback((file: DeviceFile): string => (
    file?.name || ''
  ), []);

  // delegates to filterOptions, providing the current search field ...
  const getFilterOptions = useCallback((options: DeviceFile[], {inputValue}: {inputValue: string}) => (
    filterOptions(options, field, inputValue)
  ), [field]);

  return (
    <div className={classes.container}>
      <Autocomplete classes={styles.dropdownProps}
        onChange={onChange}
        renderOption={onRenderFileNameOption}
        getOptionLabel={getOptionLabel}
        filterOptions={getFilterOptions}
        options={deviceFiles}
        defaultValue={{name: task?.metaData?.title}}
        disabled={isLoading}
        openOnFocus={false}
        disableClearable
        noOptionsText={translate('jobcreate:search.lmuconfig.noResults')}
        renderInput={function(params) {
          return (
            <JobTaskSearchInput isLoading={isLoading}
              placeholder={''}
              params={params} />
          );
        }} />
    </div>
  );
};
export default JobTaskLMUConfigSearch;
